import wam from '@tcc/wam';
import { log } from 'util/logging';
import { get } from 'lodash';

import { treatErrorAs } from 'api/errors';
import { errorLevel } from 'constants/errors';
import { createTrackingClosure } from 'util/tools';
import { rejectAfter, resolveAfter, neverResolve } from 'util/promise';
import {
  DEFAULT_POLLING_INTERVAL,
  DEFAULT_POLLING_TIMEOUT,
  POLLING_TIMEOUT_MSG,
  POLLING_ABORT_MSG
} from 'constants/api';

const status = {
  COMPLETE: 1,
  FAILURE: -1,
  PENDING: 0,
  PREPARING: -3, // bulk
  TIMEOUT: -2
};

export const authenticate = () => {
  return new Promise( (resolve) => {
    wam.on('authenticated', () => {
      log('User Authenticated');
      resolve();
    });
    wam.on('error', function(error) {
      log('WAM error: ', error);
      treatErrorAs({
        severity: errorLevel.REAUTHENTICATE,
        message: `401 from wam`,
        andStopHere: true,
        andHandleNow: true
      })(error);
    });
    log('Starting Authentication');
    wam.authenticate();
  });
};

export const getCacheBuster = () => (new Date()).getTime();

const pollLoop = ({
  callToPoll,
  secondsToWait,
  prevResults,
  parseInterval,
  pollUntil,
  abortIf,
  requestIsDone,
  appIsDead = () => {}
}) => {
  return callToPoll( prevResults )
  .then( result => {

    if( requestIsDone() ) { // Externally ended (e.g. overall timeout)
      return neverResolve();
    }

    if(appIsDead()) {
      return neverResolve();
    }

    if( abortIf && abortIf(result) ) { // User-specified
      requestIsDone(true);
      return Promise.reject(POLLING_ABORT_MSG);
    }

    if( pollUntil(result) ) {
      return result;
    }

    return resolveAfter(secondsToWait) // Retry
    .then( () => {
      const interval = parseInterval(result) || DEFAULT_POLLING_INTERVAL;
      const prevResults = result;
      return pollLoop({
        callToPoll,
        secondsToWait: interval,
        prevResults,
        parseInterval,
        abortIf,
        pollUntil,
        requestIsDone
      });
    });
  }); // Caller must catch
};


export const pollRepeatedly = ({
  callToPoll, // func passed results, gives promise that resolves with new results
  pollUntil = () => true, // func that returns true when ready to stop, is passed callToPoll call results
  parseInterval = () => DEFAULT_POLLING_INTERVAL, // func that returns a time interval (seconds), is passed callToPoll call results
  abortIf = null, // func that returns true if the polling should abort and reject, is passed callToPoll call results
  timeLimit = DEFAULT_POLLING_TIMEOUT, // total number seconds before stopping the cycle
  appIsDead = () => {} // Stop polling if we have a fatal error elsewhere
}) => {

  const requestIsDone = createTrackingClosure( false );

  const pollster = pollLoop({
    callToPoll,
    secondsToWait: DEFAULT_POLLING_INTERVAL,
    parseInterval,
    pollUntil,
    abortIf,
    requestIsDone,
    appIsDead
  });

  const pollPromise = timeLimit
    ? Promise.race([ // Race the overall timeout vs the series of polling requests
      rejectAfter( timeLimit, POLLING_TIMEOUT_MSG ),
      pollster
    ])
    : pollster;

  return pollPromise
  .then( result => requestIsDone(true) && result )
  .catch( err => requestIsDone(true) && Promise.reject(err) );
};

export const ignorePollingAbort = () => {
  return err => {
    return err === POLLING_ABORT_MSG ? null : Promise.reject(err);
  };
};

// This is poll for SOME services, but not all.  TODO: rename/replace/rejigger
export const poll = (requesterToPoll) => {
  return new Promise(
    (resolve, reject) => {

      const evalResponse = (pollingResponse) => {
        const retry = get(pollingResponse, 'retry-interval');
        const scriptReturned = !!get(pollingResponse, 'fmz-id');
        const jobStatus = get(pollingResponse, 'jobStatus');

        if (retry || jobStatus === status.PREPARING || jobStatus === status.PENDING) {

          const retryInterval = get(retry, 'q', DEFAULT_POLLING_INTERVAL) * 1000;
          setTimeout(() => {
            requesterToPoll().then(evalResponse).catch( (err) => reject(err) );
          }, retryInterval);

        } else if (scriptReturned) {
          resolve(pollingResponse);
        } else if (jobStatus === status.COMPLETE) {
          resolve('complete');
        } else if (jobStatus === status.FAILURE || jobStatus === status.TIMEOUT) {
          reject(jobStatus);
        } else if (!jobStatus) {
          reject(pollingResponse);
        }

      };
      requesterToPoll().then(evalResponse).catch( (err) => reject(err) );
    }
  );
};

export const unitTestPrivate = {
  status
};
